import React, { createContext, useState, useEffect, ReactNode } from 'react'

// Tipos
export type RegionCode = 'MX' | 'US'
export type CurrencyCode = 'MXN' | 'USD'

export interface RegionData {
  region: RegionCode
  currency: CurrencyCode
  country: string
  isLoading: boolean
  error: string | null
}

interface RegionContextType extends RegionData {
  setRegion: (region: RegionCode) => void
}

// Valores por defecto (México como fallback seguro)
const DEFAULT_REGION: RegionData = {
  region: 'MX',
  currency: 'MXN',
  country: 'Mexico',
  isLoading: true,
  error: null
}

// Crear contexto
export const RegionContext = createContext<RegionContextType>({
  ...DEFAULT_REGION,
  setRegion: () => {}
})

// Clave para localStorage
const STORAGE_KEY = 'innovagent_region'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 horas en ms

interface CachedRegion {
  region: RegionCode
  currency: CurrencyCode
  country: string
  timestamp: number
}

// Provider
interface RegionProviderProps {
  children: ReactNode
}

export const RegionProvider: React.FC<RegionProviderProps> = ({ children }) => {
  const [regionData, setRegionData] = useState<RegionData>(DEFAULT_REGION)

  // Función para determinar región basada en país
  const getRegionFromCountry = (country: string): { region: RegionCode; currency: CurrencyCode } => {
    // México = MXN, todo lo demás = USD
    if (country.toLowerCase() === 'mexico' || country.toLowerCase() === 'méxico') {
      return { region: 'MX', currency: 'MXN' }
    }
    return { region: 'US', currency: 'USD' }
  }

  // Función para cambiar región manualmente (por si se necesita en futuro)
  const setRegion = (newRegion: RegionCode) => {
    const currency: CurrencyCode = newRegion === 'MX' ? 'MXN' : 'USD'
    const country = newRegion === 'MX' ? 'Mexico' : 'United States'
    
    const newData: RegionData = {
      region: newRegion,
      currency,
      country,
      isLoading: false,
      error: null
    }
    
    setRegionData(newData)
    
    // Guardar en cache
    const cached: CachedRegion = {
      region: newRegion,
      currency,
      country,
      timestamp: Date.now()
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cached))
    } catch (e) {
      console.warn('Could not save region to localStorage:', e)
    }
  }

  useEffect(() => {
    const detectRegion = async () => {
      // 1. Intentar obtener del cache
      try {
        const cachedStr = localStorage.getItem(STORAGE_KEY)
        if (cachedStr) {
          const cached: CachedRegion = JSON.parse(cachedStr)
          const isValid = Date.now() - cached.timestamp < CACHE_DURATION
          
          if (isValid && cached.region && cached.currency) {
            setRegionData({
              region: cached.region,
              currency: cached.currency,
              country: cached.country,
              isLoading: false,
              error: null
            })
            return
          }
        }
      } catch (e) {
        console.warn('Could not read region from localStorage:', e)
      }

      // 2. Detectar por IP usando API gratuita
      try {
        // Usamos ip-api.com (gratuito, no requiere API key, 45 req/min)
        // IMPORTANTE: Solo funciona con HTTP en desarrollo, HTTPS en producción usar alternativa
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5s timeout
        
        const response = await fetch('https://ipapi.co/json/', {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json'
          }
        })
        
        clearTimeout(timeoutId)
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        
        const data = await response.json()
        
        if (data.error) {
          throw new Error(data.reason || 'API error')
        }
        
        const country = data.country_name || 'Unknown'
        const { region, currency } = getRegionFromCountry(country)
        
        // Guardar en cache
        const cached: CachedRegion = {
          region,
          currency,
          country,
          timestamp: Date.now()
        }
        
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(cached))
        } catch (e) {
          console.warn('Could not save region to localStorage:', e)
        }
        
        setRegionData({
          region,
          currency,
          country,
          isLoading: false,
          error: null
        })
        
      } catch (error) {
        console.warn('Region detection failed, using default (MX):', error)
        
        // Fallback a México si falla la detección
        setRegionData({
          region: 'MX',
          currency: 'MXN',
          country: 'Mexico',
          isLoading: false,
          error: error instanceof Error ? error.message : 'Detection failed'
        })
      }
    }

    detectRegion()
  }, [])

  return (
    <RegionContext.Provider value={{ ...regionData, setRegion }}>
      {children}
    </RegionContext.Provider>
  )
}
